import React, { useEffect, useState, useMemo } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import CalendarPage from "./Calendar";
import { useAuth } from "../context/AuthContext";

import { buildRuntimeRoadmapFromFirestore } from "../utils/buildRuntimeRoadmapFromFirestore";
import { getLocalDateKey } from "../utils/date";
import styled from "styled-components";
import { shimmer } from "../components/shimmer/shimmer";
import { ArrowRight, CalendarDays } from "lucide-react";
import { useNavigate } from "react-router-dom";

/* ---------------- Helpers ---------------- */

const flattenRuntimeDays = (runtimeRoadmap, startedAt) => {
  if (!startedAt) return [];

  const startDate = startedAt.toDate ? startedAt.toDate() : new Date(startedAt);

  const base = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );

  const flatDays = [];
  let absoluteDayIndex = 0;

  runtimeRoadmap.forEach((phase) => {
    phase.weeks.forEach((week) => {
      week.days.forEach((day) => {
        const date = new Date(base);
        date.setDate(base.getDate() + absoluteDayIndex);

        flatDays.push({
          dayId: day.id,
          title: day.title || day.topic,
          phaseKey: phase.phaseKey,
          week: week.week,
          day: day.day,
          absoluteDayIndex,
          dayNumber: absoluteDayIndex + 1,
          date: getLocalDateKey(date),
        });

        absoluteDayIndex++;
      });
    });
  });

  return flatDays;
};

/* ---------------- Skeleton ---------------- */
const SkeletonPage = styled.div`
  min-height: 100vh;
  padding: 116px 20px 64px;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const SkeletonShell = styled.div`
  max-width: 1140px;
  margin: 0 auto 28px;
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
`;

const SkeletonBlock = styled.div`
  height: ${(p) => p.h || "14px"};
  width: ${(p) => p.w || "100%"};
  border-radius: 8px;

  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.08) 25%,
    rgba(255, 255, 255, 0.16) 37%,
    rgba(255, 255, 255, 0.08) 63%
  );

  background-size: 400% 100%;
  animation: ${shimmer} 1.4s ease infinite;
`;

const SkeletonHero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(260px, 0.9fr);
  gap: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SkeletonPanel = styled.div`
  padding: 22px;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.68);
  border: 1px solid rgba(148, 163, 184, 0.1);
`;

const SkeletonStatsGrid = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 920px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const SkeletonStatCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const SkeletonLegend = styled.div`
  margin-top: 18px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const SkeletonMonthCard = styled.div`
  max-width: 1140px;
  margin: 0 auto 32px;
  padding: 20px;
  border-radius: 28px;
  background: rgba(15, 23, 42, 0.62);
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
`;

const SkeletonMonthHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  margin-bottom: 14px;
`;

const SkeletonGridScroller = styled.div`
  overflow-x: auto;
  padding-bottom: 4px;
`;

const SkeletonWeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(44px, 1fr));
  gap: 10px;
  min-width: 720px;
`;

const SkeletonWeekday = styled.div`
  padding-bottom: 6px;
`;

const SkeletonDay = styled.div`
  margin-top: 12px;
  padding: 14px;
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.55);
`;

const SkeletonRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const SkeletonCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const CalendarSkeleton = () => {
  return (
    <SkeletonPage>
      <SkeletonShell>
        <SkeletonHero>
          <div>
            <SkeletonBlock h="34px" w="180px" style={{ borderRadius: 999 }} />
            <div style={{ marginTop: 16 }}>
              <SkeletonBlock h="42px" w="72%" />
            </div>
            <div style={{ marginTop: 12 }}>
              <SkeletonBlock h="14px" w="88%" />
            </div>
            <div style={{ marginTop: 10 }}>
              <SkeletonBlock h="14px" w="76%" />
            </div>
          </div>

          <SkeletonPanel>
            <SkeletonBlock h="12px" w="120px" />
            <div style={{ marginTop: 14 }}>
              <SkeletonBlock h="34px" w="110px" />
            </div>
            <div style={{ marginTop: 12 }}>
              <SkeletonBlock h="12px" w="92%" />
            </div>
            <div style={{ marginTop: 10 }}>
              <SkeletonBlock h="12px" w="72%" />
            </div>
          </SkeletonPanel>
        </SkeletonHero>

        <SkeletonStatsGrid>
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonStatCard key={index}>
              <SkeletonBlock h="12px" w="90px" />
              <div style={{ marginTop: 12 }}>
                <SkeletonBlock h="28px" w="56px" />
              </div>
              <div style={{ marginTop: 10 }}>
                <SkeletonBlock h="10px" w="96%" />
              </div>
              <div style={{ marginTop: 8 }}>
                <SkeletonBlock h="10px" w="72%" />
              </div>
            </SkeletonStatCard>
          ))}
        </SkeletonStatsGrid>

        <SkeletonLegend>
          <SkeletonBlock h="34px" w="84px" style={{ borderRadius: 999 }} />
          <SkeletonBlock h="34px" w="102px" style={{ borderRadius: 999 }} />
          <SkeletonBlock h="34px" w="82px" style={{ borderRadius: 999 }} />
          <SkeletonBlock h="34px" w="96px" style={{ borderRadius: 999 }} />
          <SkeletonBlock h="12px" w="220px" style={{ marginTop: 10 }} />
        </SkeletonLegend>
      </SkeletonShell>

      <SkeletonMonthCard>
        <SkeletonMonthHeader>
          <div>
            <SkeletonBlock h="24px" w="180px" />
            <div style={{ marginTop: 8 }}>
              <SkeletonBlock h="12px" w="170px" />
            </div>
          </div>
          <SkeletonBlock h="34px" w="132px" style={{ borderRadius: 999 }} />
        </SkeletonMonthHeader>

        <SkeletonGridScroller>
          <SkeletonWeekGrid>
            {Array.from({ length: 7 }).map((_, i) => (
              <SkeletonWeekday key={`weekday-${i}`}>
                <SkeletonBlock h="12px" w="24px" />
              </SkeletonWeekday>
            ))}

            {Array.from({ length: 35 }).map((_, i) => (
              <SkeletonDay key={i} style={{ padding: 10, minHeight: 120, marginTop: 0 }}>
                <SkeletonCol>
                  <SkeletonRow style={{ justifyContent: "space-between" }}>
                    <SkeletonBlock h="18px" w="28px" />
                    <SkeletonBlock h="10px" w="38px" />
                  </SkeletonRow>
                  <SkeletonBlock h="11px" w="92%" />
                  <SkeletonBlock h="11px" w="68%" />
                  <SkeletonBlock h="20px" w="74px" style={{ borderRadius: 999 }} />
                </SkeletonCol>
              </SkeletonDay>
            ))}
          </SkeletonWeekGrid>
        </SkeletonGridScroller>
      </SkeletonMonthCard>
    </SkeletonPage>
  );
};

const EmptyState = styled.div`
  min-height: 100vh;
  padding: 120px 20px 64px;
  display: flex;
  justify-content: center;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const EmptyCard = styled.div`
  width: 100%;
  max-width: 760px;
  padding: 28px;
  border-radius: 28px;
  background: rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.4);
`;

const EmptyEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
`;

const EmptyTitle = styled.h1`
  margin: 16px 0 0;
  color: #f8fafc;
  font-size: 34px;
  line-height: 1.1;
  font-weight: 1000;
`;

const EmptySub = styled.p`
  margin: 12px 0 0;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.7;
`;

const EmptyAction = styled.button`
  margin-top: 20px;
  padding: 12px 18px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #111827;
  font-size: 14px;
  font-weight: 900;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

/* ---------------- Component ---------------- */

export default function CalendarContainer() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [roadmap, setRoadmap] = useState(null);
  const [runtimeRoadmap, setRuntimeRoadmap] = useState([]);

  /* ---------------- Roadmap Doc ---------------- */
  useEffect(() => {
    if (!user) return;

    const ref = doc(db, "roadmaps", user.uid);

    const unsub = onSnapshot(ref, async (snap) => {
      if (!snap.exists()) {
        setLoading(false);
        return;
      }

      const data = snap.data();
      setRoadmap(data);

      const built = await buildRuntimeRoadmapFromFirestore(data.roadmapRef);
      setRuntimeRoadmap(built);

      setLoading(false);
    });

    return () => unsub();
  }, [user]);

  /* ---------------- Flatten for Calendar ---------------- */
  const flatDays = useMemo(() => {
    if (!roadmap?.startedAt || !runtimeRoadmap.length) return [];
    return flattenRuntimeDays(runtimeRoadmap, roadmap.startedAt);
  }, [runtimeRoadmap, roadmap?.startedAt]);

  /* ---------------- UI ---------------- */

  if (loading) {
    return <CalendarSkeleton />;
  }

  if (!roadmap) {
    return (
      <EmptyState>
        <EmptyCard>
          <EmptyEyebrow>
            <CalendarDays size={14} />
            Calendar Planning
          </EmptyEyebrow>
          <EmptyTitle>No roadmap found yet</EmptyTitle>
          <EmptySub>
            Your calendar becomes useful once your roadmap is created and
            started. Generate your roadmap first, then come back here to plan
            your execution by date.
          </EmptySub>
          <EmptyAction onClick={() => navigate("/my-roadmap")}>
            <ArrowRight size={16} />
            Go to My Roadmap
          </EmptyAction>
        </EmptyCard>
      </EmptyState>
    );
  }

  return (
    <CalendarPage
      completedDays={roadmap.completedDays || {}}
      reminderTime={roadmap.reminderTime}
      flatDays={flatDays}
    />
  );
}
